import React, { useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import type { LensDataset, LensFilter } from '~/types/leLens';

import NativeLensFilter from './NativeLensFilter';
import { type FilterColumnOption, datasetsMissingColumn, dimensionColumnsAcrossDatasets } from './filterColumnCoverage';

export default function AddLensFilterDialog({
  open,
  filter,
  onClose,
  onDelete,
  onSave,
  isSaving,
  existingIds,
  datasets = [],
}: {
  open: boolean;
  filter?: LensFilter | null;
  onClose: () => void;
  onDelete?: (filter: LensFilter) => Promise<void>;
  onSave: (filter: LensFilter) => Promise<void>;
  isSaving: boolean;
  existingIds: Array<string>;
  /** Every dataset on the board (F9), so the data-field picker can list dimension columns with
   *  the datasets each appears in. Omitted or empty falls back to a plain free-text field, for a
   *  caller not yet passing the board's datasets. */
  datasets?: ReadonlyArray<LensDataset>;
}) {
  const [label, setLabel] = useState(filter?.label ?? '');
  const [column, setColumn] = useState(filter?.column ?? '');
  const [type, setType] = useState<LensFilter['type']>('select');
  const [options, setOptions] = useState(filter?.options?.join(', ') ?? '');
  const [defaultSelect, setDefaultSelect] = useState(
    typeof filter?.defaultValue === 'string'
      ? filter.defaultValue
      : Array.isArray(filter?.defaultValue)
        ? filter.defaultValue.join(', ')
        : '',
  );
  const columnOptions = useMemo(() => {
    if (type === 'select' || type === 'boolean') return dimensionColumnsAcrossDatasets(datasets);
    const options = new Map<string, FilterColumnOption>();
    for (const dataset of datasets) {
      for (const field of dataset.columns ?? []) {
        const numeric = ['INT64', 'INTEGER', 'FLOAT64', 'FLOAT', 'NUMERIC', 'BIGNUMERIC', 'DECIMAL'].includes(field.type?.toUpperCase() ?? '');
        if (!(type === 'dateRange' ? field.kind === 'date' : numeric)) continue;
        const key = field.name.toLowerCase();
        const existing = options.get(key);
        if (existing) existing.datasets.push(dataset);
        else options.set(key, { name: field.name, label: field.label ?? field.name, datasets: [dataset] });
      }
    }
    return [...options.values()].sort((left, right) => left.label.localeCompare(right.label));
  }, [datasets, type]);
  const hasColumnOptions = columnOptions.length > 0;
  const selectedOption = columnOptions.find((option) => option.name.toLowerCase() === column.trim().toLowerCase());
  const [multiple, setMultiple] = useState(filter?.multiple ?? true);
  const [defaultFrom, setDefaultFrom] = useState(
    isDateRangeDefault(filter?.defaultValue) ? filter.defaultValue.from : '',
  );
  const [defaultTo, setDefaultTo] = useState(isDateRangeDefault(filter?.defaultValue) ? filter.defaultValue.to : '');
  const [nativeDefault, setNativeDefault] = useState<LensFilter['defaultValue']>(filter?.defaultValue);
  const [nativeDefaultValid, setNativeDefaultValid] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLabel(filter?.label ?? '');
    setColumn(filter?.column ?? '');
    setType(filter?.type ?? 'select');
    setNativeDefault(filter?.defaultValue);
    setNativeDefaultValid(true);
    setOptions(filter?.options?.join(', ') ?? '');
    setDefaultSelect(
      typeof filter?.defaultValue === 'string'
        ? filter.defaultValue
        : Array.isArray(filter?.defaultValue)
          ? filter.defaultValue.join(', ')
          : '',
    );
    setMultiple(filter?.multiple ?? true);
    setDefaultFrom(isDateRangeDefault(filter?.defaultValue) ? filter.defaultValue.from : '');
    setDefaultTo(isDateRangeDefault(filter?.defaultValue) ? filter.defaultValue.to : '');
    setError(null);
  }, [filter, open]);

  const save = async () => {
    if (!nativeDefaultValid) return setError('Minimum must not exceed maximum.');
    const nextLabel = label.trim();
    const nextColumn = column.trim();
    if (hasColumnOptions && !selectedOption) return setError('Choose a field from the dataset list.');
    if (!nextLabel) {
      return setError('A filter label is required.');
    }
    if (type === 'dateRange' && Boolean(defaultFrom) !== Boolean(defaultTo)) {
      return setError('Set both default dates, or leave both blank.');
    }
    const id = filter?.id ?? uniqueId(nextLabel, existingIds);
    await onSave({
      id,
      label: nextLabel,
      ...(nextColumn ? { column: nextColumn } : {}),
      type,
      ...((type === 'boolean' || type === 'numberRange') && nativeDefault !== undefined && nativeDefault !== ''
        ? { defaultValue: nativeDefault }
        : {}),
      ...(type === 'select' && multiple ? { multiple: true } : {}),
      ...(type === 'select' && defaultSelect.trim()
        ? {
            defaultValue: multiple
              ? defaultSelect
                  .split(',')
                  .map((value) => value.trim())
                  .filter(Boolean)
              : defaultSelect.trim(),
          }
        : {}),
      ...(type === 'dateRange' && defaultFrom && defaultTo
        ? { defaultValue: { from: defaultFrom, to: defaultTo } }
        : {}),
      ...(type === 'select' && options.trim()
        ? {
            options: options
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean),
          }
        : {}),
    });
    if (!filter) {
      setLabel('');
      setColumn('');
      setOptions('');
      setDefaultSelect('');
      setDefaultFrom('');
      setDefaultTo('');
    }
    setError(null);
  };
  return (
    <Dialog fullWidth maxWidth="sm" onClose={isSaving ? undefined : onClose} open={open}>
      <DialogTitle>{filter ? 'Edit Lens filter' : 'Add Lens filter'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info">
            Choose a dataset field. This filter automatically applies to cards whose dataset contains it. Its values are
            loaded from the data when you save.
          </Alert>
          <TextField
            autoFocus
            fullWidth
            label="Filter label"
            onChange={(event) => setLabel(event.target.value)}
            value={label}
          />
          {hasColumnOptions ? (
            <Stack spacing={0.5}>
              <Autocomplete<FilterColumnOption, false, false, true>
                autoHighlight
                filterOptions={(candidates, state) =>
                  candidates.filter((candidate) =>
                    `${candidate.name} ${candidate.label}`
                      .toLowerCase()
                      .includes(state.inputValue.trim().toLowerCase()),
                  )
                }
                freeSolo
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
                inputValue={column}
                onChange={(_event, value) => {
                  setColumn(typeof value === 'string' ? value : (value?.name ?? ''));
                  if (!label && value && typeof value !== 'string') setLabel(value.label);
                }}
                onInputChange={(_event, value) => setColumn(value)}
                options={columnOptions}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    helperText="Applies to cards using a dataset with this field."
                    label="Data field"
                  />
                )}
                renderOption={(props, option: FilterColumnOption) => (
                  <li {...props} key={option.name}>
                    <Stack spacing={0}>
                      <Typography variant="body2">{option.label}</Typography>
                      <Typography color="text.secondary" variant="caption">
                        {option.name} · in {option.datasets.length} of {datasets.length}{' '}
                        {datasets.length === 1 ? 'dataset' : 'datasets'}
                      </Typography>
                    </Stack>
                  </li>
                )}
              />
              {column.trim() &&
                (selectedOption ? (
                  <Typography color="text.secondary" variant="caption">
                    In {selectedOption.datasets.length} of {datasets.length}{' '}
                    {datasets.length === 1 ? 'dataset' : 'datasets'}:{' '}
                    {selectedOption.datasets.map((d) => d.name).join(', ')}
                    {datasetsMissingColumn(datasets, column).length > 0 &&
                      `. Not filtered by ${selectedOption.label}: ${datasetsMissingColumn(datasets, column)
                        .map((d) => d.name)
                        .join(', ')}`}
                  </Typography>
                ) : (
                  <Typography color="text.secondary" variant="caption">
                    Not declared on any dataset yet. The filter will not bind anywhere until it is.
                  </Typography>
                ))}
            </Stack>
          ) : (
            <TextField
              fullWidth
              helperText="Optional legacy field name for queries using {{lens_filters}}. New cards use bindings instead."
              label="Legacy data field (optional)"
              onChange={(event) => setColumn(event.target.value)}
              value={column}
            />
          )}
          <FormControl fullWidth>
            <InputLabel id="lens-filter-type">Filter type</InputLabel>
            <Select
              label="Filter type"
              labelId="lens-filter-type"
              onChange={(event) => {
                setType(event.target.value);
                setNativeDefault(undefined);
                setNativeDefaultValid(true);
              }}
              value={type}
            >
              <MenuItem value="select">Dropdown</MenuItem>
              <MenuItem value="dateRange">Date range</MenuItem>
              <MenuItem value="boolean">Boolean toggle</MenuItem>
              <MenuItem value="numberRange">Numeric range</MenuItem>
            </Select>
          </FormControl>
          {(type === 'boolean' || type === 'numberRange') && (
            <NativeLensFilter
              filter={{ id: filter?.id ?? 'default', label: 'Default value', type }}
              value={nativeDefault}
              onChange={(value) => setNativeDefault(value as LensFilter['defaultValue'])}
              onValidityChange={setNativeDefaultValid}
            />
          )}
          {type === 'select' && (
            <>
              <FormControlLabel
                control={<Switch checked={multiple} onChange={(event) => setMultiple(event.target.checked)} />}
                label="Allow multiple selections"
              />
              <TextField
                fullWidth
                helperText="Optional. Leave blank to load values from the dataset."
                label="Options"
                onChange={(event) => setOptions(event.target.value)}
                value={options}
              />
              <TextField
                fullWidth
                helperText={multiple ? 'Optional comma-separated initial selections.' : 'Optional initial selection.'}
                label="Default value"
                onChange={(event) => setDefaultSelect(event.target.value)}
                value={defaultSelect}
              />
            </>
          )}
          {type === 'dateRange' && (
            <Stack direction="row" spacing={1}>
              <TextField
                InputLabelProps={{ shrink: true }}
                fullWidth
                label="Default from"
                onChange={(event) => setDefaultFrom(event.target.value)}
                type="date"
                value={defaultFrom}
              />
              <TextField
                InputLabelProps={{ shrink: true }}
                fullWidth
                label="Default to"
                onChange={(event) => setDefaultTo(event.target.value)}
                type="date"
                value={defaultTo}
              />
            </Stack>
          )}
          {error && (
            <Button color="error" disabled>
              {error}
            </Button>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {filter && onDelete && (
          <Button color="error" disabled={isSaving} onClick={() => onDelete(filter)} sx={{ mr: 'auto' }}>
            Remove filter
          </Button>
        )}
        <Button disabled={isSaving} onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSaving || !nativeDefaultValid} onClick={save} variant="contained">
          {isSaving ? 'Saving…' : filter ? 'Save filter' : 'Add filter'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Exported so a filter minted programmatically (the scope-confirmation gate's own geography
 *  filter, for one) gets the same slug-from-label id a person typing into this dialog would. */
export function uniqueId(label: string, existingIds: Array<string>): string {
  const base =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'filter';
  const ids = new Set(existingIds);
  if (!ids.has(base)) {
    return base;
  }
  let suffix = 2;
  while (ids.has(`${base}-${suffix}`)) {
    suffix += 1;
  }
  return `${base}-${suffix}`;
}

function isDateRangeDefault(value: LensFilter['defaultValue'] | undefined): value is { from: string; to: string } {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value) && 'from' in value && 'to' in value);
}
